import { useState, useRef, useContext, useCallback, memo, useEffect } from "react";
import styles from "../../docxTemplateWidget.module.css";
import { AppContext } from "../../../../../../../../contexts/AppContext";
import { CustomField } from "../../../../../../../../sdkStructs";

interface CustomFieldsSectionProps {
    customFields: CustomField[];
    onChange: (fields: CustomField[]) => void;
}

// Sanitize field name to only allow letters
function sanitizeFieldName(Name: string): string {
    return Name.replace(/[^a-zA-Z]/g, "");
}

// Individual field component
const CustomFieldItem = memo(({
    field,
    onNameChange,
    onValueChange,
    onRemove
}: {
    field: CustomField;
    onNameChange: (newName: string) => void;
    onValueChange: (newValue: string) => void;
    onRemove: () => void;
}) => {
    // Update directly without debouncing
    return (
        <div className={styles.customFieldItem}>
            <div className={styles.customFieldHeader}>
                <input
                    type="text"
                    value={field.Name}
                    onChange={(e) => onNameChange(sanitizeFieldName(e.target.value))}
                    className={styles.customFieldNameInput}
                    placeholder="Field name"
                    pattern="[a-zA-Z]+"
                    title="Only letters allowed"
                    aria-label={`Field name: ${field.Name || "Unnamed field"}`}
                />
                <button
                    onClick={onRemove}
                    className={styles.removeFieldButton}
                    title="Remove field"
                    aria-label={`Remove ${field.Name || "field"}`}
                >
                    ×
                </button>
            </div>
            <textarea
                value={field.Value}
                onChange={(e) => onValueChange(e.target.value)}
                className={styles.textarea}
                placeholder={`Enter your ${field.Name || "field"} content here...`}
                rows={4}
                aria-label={`${field.Name || "Field"} content`}
            />
        </div>
    );
});

function CustomFieldsSection({ customFields, onChange }: CustomFieldsSectionProps) {
    const { pushAlert } = useContext(AppContext);
    const [fields, setFields] = useState<CustomField[]>(customFields);
    const [newFieldName, setNewFieldName] = useState("");
    const newFieldInputRef = useRef<HTMLInputElement>(null);

    // Update internal state when customFields changes
    useEffect(() => {
        setFields(customFields);
    }, [customFields]);

    // Update parent with changes (using a callback)
    const updateFields = useCallback((newFields: CustomField[]) => {
        setFields(newFields);
        // Send changes to the TemplateEditor
        onChange(newFields);
        console.log("Fields updated and sent to TemplateEditor:", newFields);
    }, [onChange]);

    // Add a new field
    const handleAddField = useCallback(() => {
        if (!newFieldName.trim()) return;

        const sanitizedName = sanitizeFieldName(newFieldName);
        if (!sanitizedName) {
            pushAlert({
                title: "Error",
                text: "Field name must contain at least one letter",
                type: "error",
                closeButton: true,
                time: 3000
            });
            return;
        }

        // Check for duplicate name
        if (fields.some(field => field.Name.toLowerCase() === sanitizedName.toLowerCase())) {
            pushAlert({
                title: "Error",
                text: "This field name already exists",
                type: "error",
                closeButton: true,
                time: 3000
            });
            return;
        }

        const newField: CustomField = {
            Name: sanitizedName,
            Value: ""
        };

        updateFields([...fields, newField]);
        setNewFieldName("");

        // Focus the input field after adding
        setTimeout(() => {
            if (newFieldInputRef.current) {
                newFieldInputRef.current.focus();
            }
        }, 0);
    }, [fields, newFieldName, pushAlert, updateFields]);

    // Update field name
    const handleNameChange = useCallback((index: number, newName: string) => {
        if (!newName) return;

        // Check for duplicate name
        if (fields.some((field, i) => i !== index && field.Name.toLowerCase() === newName.toLowerCase())) {
            pushAlert({
                title: "Error",
                text: "This field name already exists",
                type: "error",
                closeButton: true,
                time: 3000
            });
            return;
        }

        const updatedFields = [...fields];
        updatedFields[index].Name = newName;
        updateFields(updatedFields);
    }, [fields, pushAlert, updateFields]);

    // Update field value
    const handleValueChange = useCallback((index: number, newValue: string) => {
        const updatedFields = [...fields];
        updatedFields[index].Value = newValue;
        updateFields(updatedFields);
    }, [fields, updateFields]);

    // Remove a field
    const handleRemoveField = useCallback((index: number) => {
        const updatedFields = fields.filter((_, i) => i !== index);
        updateFields(updatedFields);
    }, [fields, updateFields]);

    // Handle enter key in the new field input
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddField();
        }
    }, [handleAddField]);

    return (
        <section className={styles.section} aria-labelledby="custom-fields-title">
            <h3 id="custom-fields-title" className={styles.sectionTitle}>Custom Fields</h3>
            <p className={styles.sectionDescription}>Field names must contain only letters (a-z, A-Z).</p>

            {fields.length === 0 && (
                <p className={styles.emptyState}>No custom fields added yet. Add a field below.</p>
            )}

            <div className={styles.customFieldsList} role="list">
                {fields.map((field, index) => (
                    <CustomFieldItem
                        key={`field-${index}`}
                        field={field}
                        onNameChange={(Name) => handleNameChange(index, Name)}
                        onValueChange={(Value) => handleValueChange(index, Value)}
                        onRemove={() => handleRemoveField(index)}
                    />
                ))}
            </div>

            <div className={styles.addFieldContainer}>
                <div className={styles.addFieldInputWrapper}>
                    <input
                        type="text"
                        value={newFieldName}
                        onChange={(e) => setNewFieldName(sanitizeFieldName(e.target.value))}
                        onKeyDown={handleKeyDown}
                        className={styles.addFieldInput}
                        placeholder="New field name (letters only)"
                        ref={newFieldInputRef}
                        pattern="[a-zA-Z]+"
                        title="Only letters allowed"
                        required
                        aria-label="New field name"
                    />
                </div>
                <button
                    onClick={handleAddField}
                    className={styles.addFieldButton}
                    disabled={!newFieldName.trim()}
                    aria-label="Add new field"
                >
                    Add Field
                </button>
            </div>
        </section>
    );
}

export default memo(CustomFieldsSection); 
